import Box from "@mui/material/Box";
import { useCallback } from "react";
import { DataGrid, GridCellModes } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import useScreenWidth from "../../../hooks/useScreenwidth";
import { setLoading } from "../../../store/slices/loaderSlice";
import { CircularProgress } from "@mui/material";

const CustomDatagrid = ({
  rows,
  setRows, // Function to update row state
  columns,
  CustomNoRowsOverlay,
  getRowId,
  height,
  tHeight = "33em",
  checkRequired = false,
  onCellClick,
  hoverColor = "#F5F8FA",
  onRowClick,
  isLoadingStateProvided,
  isLoading,
}) => {
  const [isRowsVisible, setIsRowsVisible] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [cellModesModel, setCellModesModel] = useState({}); // Track edit mode for individual cells
  const screenWidth = useScreenWidth();

  useEffect(() => {
    if (isLoadingStateProvided) {
      setShowLoader(isLoading);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoadingStateProvided) {
      let timeoutId;

      if (rows && rows.length > 0) {
        setIsRowsVisible(true);
        setShowLoader(false);
      } else {
        setIsRowsVisible(false);
        setShowLoader(false);
      }
    }
  }, [rows]);

  useEffect(() => {
    setIsRowsVisible(rows && rows.length > 0);
  }, [rows]);

  const dynamicHeight = isRowsVisible
    ? "auto"
    : screenWidth > 1600
    ? "60vh"
    : tHeight;

  const handleCellClick = useCallback((params, event) => {
    if (onCellClick) {
      onCellClick(params, event);
    }

    if (!params.isEditable) {
      return;
    }

    setCellModesModel((prevModel) => {
      // Convert all cells to View mode
      const updatedModel = Object.fromEntries(
        Object.entries(prevModel).map(([id, fields]) => [
          id,
          Object.fromEntries(
            Object.entries(fields).map(([field]) => [
              field,
              { mode: GridCellModes.View },
            ])
          ),
        ])
      );

      return {
        ...updatedModel,
        [params.id]: {
          ...(updatedModel[params.id] || {}),
          [params.field]: { mode: GridCellModes.Edit }, // Set clicked cell to Edit mode
        },
      };
    });
  }, []);

  const handleCellModesModelChange = useCallback((newModel) => {
    setCellModesModel(newModel);
  }, []);

  return (
    <Box
      sx={{
        minHeight: tHeight,
        marginBottom: "1.5rem",
        transition: "height 0.3s ease-in-out",
        height: dynamicHeight,
        fontFamily: "Inter, sans-serif",
        "& .MuiDataGrid-root": {
          border: "1px solid #E5E7EB", // Solid border for entire grid
          backgroundColor: "#ffffff",
        },
      }}
    >
      <div
        className={`${isRowsVisible ? "" : "table-div"} h-100 mb-3`}
        style={{ height: isRowsVisible ? "100%" : "0" }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 100 },
            },
          }}
          loading={showLoader}
          checkboxSelection={checkRequired}
          getRowHeight={height}
          disableRowSelectionOnClick
          hideFooterPagination={false}
          hideFooter
          disableColumnMenu
          editMode="cell" // Enable cell editing
          isCellEditable={(params) => params.colDef.editable === true} // Only make specific fields editable
          cellModesModel={cellModesModel}
          onCellModesModelChange={handleCellModesModelChange}
          onCellClick={handleCellClick} // Single click to enable edit mode
          sx={{
            "&.MuiDataGrid-root": {
              border: "1px solid #E5E7EB",
              fontFamily: "Nunito Sans, sans-serif",
            },
            "& .MuiDataGrid-cell": {
              border: "1px solid #E5E7EB", // Add border to each cell
              padding: "12px 16px",
              color: "#111827",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              "&:focus, &:focus-within": {
                outline: "2px solid #2563EB", // Highlight selected cell
              },
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#FCFCFC",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#FCFCFC",
              padding: "14px 16px",
              height: "48px !important",
              fontWeight: "800",
              fontSize: "14px",
              color: "#121212",
              textTransform: "uppercase",
              letterSpacing: "0.02em",
              borderBottom: "1px solid #E5E7EB", // Border for headers
              "&:focus, &:focus-within": {
                outline: "none",
              },
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "700",
              color: "#333333",
            },
            "& .MuiDataGrid-row": {
              transition: "background 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: hoverColor,
              },
            },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "#E3F2FD !important", // Light blue for selected row
              "&:hover": {
                backgroundColor: "#BBDEFB !important",
              },
            },
            "& .MuiCheckbox-root": {
              color: "#9CA3AF !important",
              "&.Mui-checked": {
                color: "#2563EB !important",
              },
            },
            "& .MuiDataGrid-virtualScroller": {
              overflow: "auto", // Enable scrolling
              
            },
            "& .MuiDataGrid-scrollbar": {
              display: "block !important",
              "&::-webkit-scrollbar": {
                width: "10px",
                height: "5px",  // Increased scrollbar height
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c1c1c1",
                borderRadius: "10px",
                height: "30px",  // Thumb height
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "10px",
                height: "30px",  // Track height
              }
            },
            "& .MuiDataGrid-filler": {
              display: "none !important",
            },
            "& .MuiDataGrid-scrollbar--vertical": {
              display: "none !important",
            },
          }}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            loadingOverlay: () => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <CircularProgress
                  sx={{ color: "#6C5DD3", marginBottom: "1rem" }}
                  size={40}
                />
              </Box>
            ),
          }}
          disableColumnFilter
        />
      </div>
    </Box>
  );
};

export default CustomDatagrid;
